import React, { useEffect, useCallback, useMemo } from 'react';
import { initPerformanceMonitoring } from '../lib/performance';

interface PerformanceOptimizerProps {
  children: React.ReactNode;
}

const PerformanceOptimizer: React.FC<PerformanceOptimizerProps> = ({ children }) => {
  // 初始化性能监控
  useEffect(() => {
    initPerformanceMonitoring();
  }, []);

  // DNS预解析和预连接
  useEffect(() => {
    const dnsPrefetchDomains = [
      'https://images.igdb.com',
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://cdn.jsdelivr.net'
    ];

    const preconnectDomains = [
      'https://images.igdb.com',
      'https://fonts.gstatic.com'
    ];

    // 添加DNS预解析
    dnsPrefetchDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = domain;
      document.head.appendChild(link);
    });

    // 添加预连接
    preconnectDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });

    // 清理函数
    return () => {
      // 在实际应用中，通常不需要移除这些预加载链接
      // 因为它们有助于整个会话的性能
    };
  }, []);

  // 关键CSS内联优化
  useEffect(() => {
    // 添加关键CSS内联样式来改善FCP
    const criticalCSS = `
      /* 关键渲染路径CSS */
      body { 
        font-display: swap; 
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      
      /* 避免布局偏移 */
      img { 
        content-visibility: auto;
        contain-intrinsic-size: 300px 200px;
      }
      
      /* 优化字体加载 */
      @font-face {
        font-family: system-ui;
        font-display: swap;
      }
    `;

    const style = document.createElement('style');
    style.id = 'critical-css';
    style.textContent = criticalCSS;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById('critical-css');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // 图像预加载策略
  const preloadCriticalImages = useCallback(() => {
    // 预加载首屏关键图像
    const criticalImages = [
      '/icons/icon-192x192.svg',
      '/placeholder.svg'
    ];

    criticalImages.forEach(src => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    });
  }, []);

  useEffect(() => {
    preloadCriticalImages();
  }, [preloadCriticalImages]);

  // 内容可见性优化
  useEffect(() => {
    // 为非关键内容添加 content-visibility
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target as HTMLElement;
          element.style.contentVisibility = 'visible';
          observer.unobserve(element);
        }
      });
    }, {
      rootMargin: '50px'
    });

    // 观察所有游戏卡片
    const gameCards = document.querySelectorAll('[data-game-card]');
    gameCards.forEach(card => {
      observer.observe(card);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // 资源提示优化
  useEffect(() => {
    // 动态添加资源提示
    const addResourceHint = (rel: string, href: string, as?: string) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      if (as) link.as = as;
      document.head.appendChild(link);
    };

    // 预加载关键路由
    addResourceHint('prefetch', '/#/categories');
    addResourceHint('prefetch', '/#/favorites');
    
    // 预加载关键数据文件
    addResourceHint('preload', '/data/games.json', 'fetch');
  }, []);

  // Service Worker 注册优化
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // 延迟注册 Service Worker 直到页面加载完成
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then(registration => {
            console.log('SW registered: ', registration);
            
            // 检查更新
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // 有新版本可用
                    console.log('New content is available; please refresh.');
                  }
                });
              }
            });
          })
          .catch(registrationError => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }
  }, []);

  // 内存泄漏防护
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 页面被隐藏时，清理不必要的资源
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            // 清理缓存、取消网络请求等
            console.log('Page hidden, cleaning up resources...');
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 错误边界和性能监控
  useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      console.error('Global error:', error);
      // 可以发送到错误监控服务
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      // 可以发送到错误监控服务
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <>
      {children}
    </>
  );
};

export default PerformanceOptimizer;